import React, { useEffect, useState, useCallback } from 'react';
import {
    Box,
    Grid,
    Paper,
    Typography,
    Card,
    CardContent,
    CardHeader,
    Divider,
    CircularProgress,
    useTheme,
    Chip,
    IconButton,
} from '@mui/material';
import {
    Assignment as ServiceOrderIcon,
    Inventory as InventoryIcon,
    People as UsersIcon,
    Warning as AlertIcon,
    Dashboard as DashboardIcon,
    Assessment as ReportingIcon,
    Storage as MasterDataIcon,
    Description as DocumentsIcon,
} from '@mui/icons-material';
import { getServiceOrders } from '../services/serviceOrders';
import { getServiceOrderStatusChipColor, getServiceOrderPriorityChipColor, formatStatusLabel } from '../constants/statusColors';
import { getEquipment, getConsumablesPaginated, getLowStockConsumables } from '../services/inventory';
import { getUsers } from '../services/users';
import PageContainer from '../components/PageContainer';
import LowStockItems from '../components/dashboard/LowStockItems';
import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from '../contexts/NotificationContext';
import { useNavigate } from 'react-router-dom';

const StatCard = ({ title, value, icon: Icon, color, loading, description, onClick, clickable = false }) => (
    <Card
        elevation={2}
        sx={{
            height: '100%',
            minHeight: 100,
            borderRadius: 4,
            cursor: clickable ? 'pointer' : 'default',
            transition: 'all 0.2s ease-in-out',
            '&:hover': clickable ? {
                boxShadow: 4,
                backgroundColor: 'action.hover',
            } : {},
        }}
        onClick={clickable ? onClick : undefined}
    >
        <CardContent sx={{ p: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                {title}
            </Typography>
            <Box display="flex" alignItems="center" gap={1.5}>
                <Icon sx={{ color: color || 'primary.main', fontSize: 28, flexShrink: 0 }} />
                {loading ? (
                    <CircularProgress size={24} />
                ) : (
                    <Typography variant="h5" component="span" fontWeight={600}>
                        {value}
                    </Typography>
                )}
            </Box>
            {description && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                    {description}
                </Typography>
            )}
        </CardContent>
    </Card>
);

const SectionCard = ({ title, description, icon: Icon, color, children, loading }) => (
    <Paper elevation={2} sx={{ p: 2, height: '100%', borderRadius: 4 }}>
        <CardHeader
            title={
                <Box display="flex" alignItems="center" gap={1}>
                    <Icon sx={{ color: color || 'primary.main', fontSize: 22 }} />
                    <Typography variant="subtitle1" fontWeight={600}>{title}</Typography>
                </Box>
            }
            subheader={description}
            subheaderTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
        />
        <Divider />
        <CardContent sx={{ pt: 2 }}>
            {loading ? (
                <Box display="flex" justifyContent="center" p={2}>
                    <CircularProgress />
                </Box>
            ) : children}
        </CardContent>
    </Paper>
);

// Quick Access card: single structure, no duplicate text, fixed min height, clickable
const QuickAccessCard = ({ title, description, icon: Icon, color, path, onClick }) => (
    <Paper
        elevation={2}
        sx={{
            height: '100%',
            minHeight: 120,
            p: 2,
            borderRadius: 4,
            display: 'flex',
            flexDirection: 'column',
            cursor: 'pointer',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
                boxShadow: 4,
                backgroundColor: 'action.hover',
            },
        }}
        onClick={() => onClick(path)}
    >
        <Box display="flex" alignItems="center" gap={1} sx={{ mb: 1 }}>
            <Icon sx={{ color: color || 'primary.main', fontSize: 22 }} />
            <Typography variant="subtitle1" fontWeight={600}>{title}</Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
            {description}
        </Typography>
    </Paper>
);

const Dashboard = () => {
    const theme = useTheme();
    const { user } = useAuth();
    const { unreadCount } = useNotifications();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState({
        serviceOrders: 0,
        inventoryItems: 0,
        users: 0,
        alerts: 0
    });
    const [recentOrders, setRecentOrders] = useState([]);
    const [lowStockItems, setLowStockItems] = useState([]);
    const [lowStockTotal, setLowStockTotal] = useState(0);

    // Navigation handlers
    const handleServiceOrdersClick = () => {
        navigate('/service-orders');
    };

    const handleInventoryClick = () => {
        navigate('/inventory');
    };

    const handleUsersClick = () => {
        navigate('/users');
    };

    const handleAlertsClick = () => {
        navigate('/notifications');
    };

    // Dynamic widget configuration based on backend permissions
    const getWidgetConfig = () => {
        if (!user?.role?.permissions) return {};

        const perms = user.role.permissions;
        
        return {
            // Service Orders widget - show if user can view service orders AND it's UI visible
            serviceOrders: {
                visible: perms.service_orders?.view === true,
                title: "Service Orders",
                description: "Service orders in the system",
                icon: ServiceOrderIcon,
                color: theme.palette.primary.main,
                value: stats.serviceOrders,
                clickable: true,
                onClick: handleServiceOrdersClick
            },
            
            // Inventory Items widget - show if user can view inventory AND it's UI visible
            inventoryItems: {
                visible: perms.inventory?.view === true,
                title: "Inventory Items", 
                description: "Equipment and consumables",
                icon: InventoryIcon,
                color: theme.palette.success.main,
                value: stats.inventoryItems,
                clickable: true,
                onClick: handleInventoryClick
            },
            
            // Users widget - show if user can view users AND it's UI visible
            users: {
                visible: perms.users?.view === true,
                title: "Users",
                description: "Registered users", 
                icon: UsersIcon,
                color: theme.palette.warning.main,
                value: stats.users,
                clickable: true,
                onClick: handleUsersClick
            },
            
            // Alerts widget — driven by unread notification count (same source as the bell badge)
            alerts: {
                visible: perms.notifications?.view === true,
                title: "Alerts",
                description: "Unread system notifications",
                icon: AlertIcon,
                color: theme.palette.error.main,
                value: unreadCount,
                clickable: true,
                onClick: handleAlertsClick
            },
            
            // Recent Service Orders section - show if user can view service orders AND it's UI visible
            recentOrders: {
                visible: perms.service_orders?.view === true,
                title: "Recent Service Orders",
                description: "Latest service orders",
                icon: ServiceOrderIcon,
                color: theme.palette.primary.main,
                data: recentOrders
            },
            
            // Low Stock Items section - show if user can view inventory AND it's UI visible
            lowStockItems: {
                visible: perms.inventory?.view === true,
                title: "Low Stock Items", 
                description: "Items that need attention",
                icon: AlertIcon,
                color: theme.palette.error.main,
                data: lowStockItems
            },
            
            // Quick Access items - based on what modules user can access AND are UI visible
            quickAccess: {
                serviceOrders: {
                    visible: perms.service_orders?.view === true,
                    title: "Service Orders",
                    description: "Manage service orders and track progress",
                    icon: ServiceOrderIcon,
                    color: theme.palette.primary.main,
                    path: '/service-orders',
                },
                inventory: {
                    visible: perms.inventory?.view === true,
                    title: "Inventory Management",
                    description: "Track equipment and consumables",
                    icon: InventoryIcon,
                    color: theme.palette.success.main,
                    path: '/inventory',
                },
                users: {
                    visible: perms.users?.view === true,
                    title: "Users",
                    description: "Manage user accounts and permissions",
                    icon: UsersIcon,
                    color: theme.palette.warning.main,
                    path: '/users',
                },
                reporting: {
                    visible: perms.reporting?.view === true,
                    title: "Reporting",
                    description: "Generate reports and analytics",
                    icon: ReportingIcon,
                    color: theme.palette.info.main,
                    path: '/reporting',
                },
                masterData: {
                    visible: perms.master_data?.view === true,
                    title: "Master Data Management",
                    description: "Manage reference data and configurations",
                    icon: MasterDataIcon,
                    color: theme.palette.secondary.main,
                    path: '/master-data',
                },
                documents: {
                    visible: perms.documents?.view === true,
                    title: "Document Management",
                    description: "Store and manage documents",
                    icon: DocumentsIcon,
                    color: theme.palette.info.main,
                    path: '/documents',
                },
                adminDashboard: {
                    visible: perms.admin_dashboard?.view === true,
                    title: "Reflection Dashboard",
                    description: "System performance, schedules, and operational metrics",
                    icon: DashboardIcon,
                    color: theme.palette.secondary.main,
                    path: '/reflection-dashboard',
                },
            },
        };
    };

    const widgetConfig = getWidgetConfig();

    // Standalone low-stock fetch — called on mount and whenever the user pages through
    const fetchLowStock = useCallback(async (page = 1) => {
        try {
            const data = await getLowStockConsumables({ page, page_size: 10 });
            const items = data?.results ?? [];
            const total = data?.count ?? items.length;
            setLowStockItems(items);
            setLowStockTotal(total);
        } catch {
            setLowStockItems([]);
            setLowStockTotal(0);
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Load data only for visible widgets
    useEffect(() => {
        const loadDashboardData = async () => {
            setLoading(true);
            try {
                const promises = [];

                // Only load service orders data if user can view service orders
                if (widgetConfig.serviceOrders?.visible) {
                    promises.push(
                        getServiceOrders().then(data => {
                            const orders = data?.results || data || [];
                            const orderCount = data?.count || orders.length;
                            setStats(prev => ({ ...prev, serviceOrders: orderCount }));
                            setRecentOrders(Array.isArray(orders) ? orders.slice(0, 5) : []);
                        })
                    );
                }

                // Only load inventory data if user can view inventory (use paginated APIs for accurate total counts)
                if (widgetConfig.inventoryItems?.visible) {
                    promises.push(
                        Promise.all([
                            getEquipment({ page: 1, page_size: 1 }),
                            getConsumablesPaginated({ page: 1, page_size: 1 }),
                            fetchLowStock(1),
                        ]).then(([equipmentData, consumablesData]) => {
                            const equipmentCount = equipmentData?.count ?? 0;
                            const consumablesCount = consumablesData?.count ?? 0;
                            setStats(prev => ({
                                ...prev,
                                inventoryItems: equipmentCount + consumablesCount,
                            }));
                        })
                    );
                }

                // Only load users data if user can view users
                if (widgetConfig.users?.visible) {
                    promises.push(
                        getUsers().then(data => {
                            const users = data?.results || data || [];
                            const userCount = data?.count || users.length;
                            setStats(prev => ({ ...prev, users: userCount }));
                        })
                    );
                }

                await Promise.all(promises);
            } catch (error) {
                console.error('Error loading dashboard data:', error);
            } finally {
                setLoading(false);
            }
        };

        loadDashboardData();
    // user?.role?.id is a stable number — avoids re-running when a new user object
    // is created but the role/permissions haven't actually changed (e.g. after the
    // background refreshUserData() call returns the same data).
    }, [user?.role?.id]); // eslint-disable-line react-hooks/exhaustive-deps

    // Render summary cards dynamically
    const renderSummaryCards = () => {
        const cards = [];
        
        Object.entries(widgetConfig).forEach(([key, config]) => {
            if (config.visible && config.value !== undefined) {
                cards.push(
                    <Grid item xs={12} sm={6} md={3} key={key}>
                        <StatCard
                            title={config.title}
                            value={config.value}
                            icon={config.icon}
                            color={config.color}
                            loading={loading}
                            description={config.description}
                            clickable={config.clickable}
                            onClick={config.onClick}
                        />
                    </Grid>
                );
            }
        });

        return cards;
    };

    // Render recent activity sections dynamically
    const renderRecentActivity = () => {
        const sections = [];
        
        // Recent Service Orders
        if (widgetConfig.recentOrders?.visible) {
            sections.push(
                <Grid item xs={12} md={8} key="recentOrders">
                    <SectionCard
                        title={widgetConfig.recentOrders.title}
                        description={widgetConfig.recentOrders.description}
                        icon={widgetConfig.recentOrders.icon}
                        color={widgetConfig.recentOrders.color}
                        loading={loading}
                    >
                        {widgetConfig.recentOrders.data?.length > 0 ? (
                            <Box>
                                {widgetConfig.recentOrders.data.map(order => (
                                    <Box 
                                        key={order.id} 
                                        mb={2}
                                        sx={{
                                            cursor: 'pointer',
                                            padding: 1,
                                            borderRadius: 1,
                                            '&:hover': {
                                                backgroundColor: 'action.hover',
                                            },
                                        }}
                                        onClick={() => navigate(`/service-orders/${order.display_name || order.id}`)}
                                    >
                                        <Typography variant="subtitle1">
                                            {(order.company?.name || 'No Company') + ' - ' + (order.service_type?.name || order.service_type?.id || order.service_type)}
                                        </Typography>
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                                            <Chip size="small" label={formatStatusLabel(order.status?.name || order.status)} color={getServiceOrderStatusChipColor(order.status?.name || order.status)} />
                                            <Chip size="small" label={formatStatusLabel(order.priority?.name || order.priority)} color={getServiceOrderPriorityChipColor(order.priority?.name || order.priority)} />
                                        </Box>
                                    </Box>
                                ))}
                            </Box>
                        ) : (
                            <Typography variant="body1">
                                No recent service orders
                            </Typography>
                        )}
                    </SectionCard>
                </Grid>
            );
        }

        // Low Stock Items (single component + API: low_stock = low consumable stock)
        if (widgetConfig.lowStockItems?.visible) {
            sections.push(
                <Grid item xs={12} md={4} key="lowStockItems">
                    <SectionCard
                        title={widgetConfig.lowStockItems.title}
                        description={widgetConfig.lowStockItems.description}
                        icon={widgetConfig.lowStockItems.icon}
                        color={widgetConfig.lowStockItems.color}
                        loading={loading}
                    >
                        <LowStockItems
                            items={lowStockItems}
                            loading={loading}
                            variant="compact"
                            totalCount={lowStockTotal}
                        />
                    </SectionCard>
                </Grid>
            );
        }

        return sections;
    };

    // Render quick access items dynamically (single description, consistent height, clickable)
    const renderQuickAccess = () => {
        const quickAccessItems = [];
        Object.entries(widgetConfig.quickAccess || {}).forEach(([key, config]) => {
            if (config.visible && config.path) {
                quickAccessItems.push(
                    <Grid item xs={12} sm={6} md={4} key={key}>
                        <QuickAccessCard
                            title={config.title}
                            description={config.description}
                            icon={config.icon}
                            color={config.color}
                            path={config.path}
                            onClick={(path) => navigate(path)}
                        />
                    </Grid>
                );
            }
        });
        return quickAccessItems;
    };

    return (
        <PageContainer>
            <Grid container spacing={3}>
                {/* Summary Cards - Dynamically rendered based on permissions */}
                {renderSummaryCards()}

                {/* Recent Activity - Dynamically rendered based on permissions */}
                {renderRecentActivity()}

                {/* Quick Access - Only show if there are visible quick access items */}
                {(() => {
                    const quickAccessItems = renderQuickAccess();
                    if (!quickAccessItems.length) return null;
                    return (
                        <>
                            <Grid item xs={12}>
                                <Typography variant="h5" gutterBottom sx={{ mt: 3, mb: 2 }}>
                                    Quick Access
                                </Typography>
                            </Grid>
                            {quickAccessItems}
                        </>
                    );
                })()}
            </Grid>
        </PageContainer>
    );
};

export default Dashboard; 